import React from "react";

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from "@mui/lab";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Theme,
  Typography,
} from "@mui/material";
import { makeStyles, withStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { ActiveClientCardFragment } from "@/gql/graphql";
import { useFragment } from "@/hooks/useFragment";
import { colors } from "@/theme/overrides/colors";
import { formatISODate } from "@/utils/datetime";
import DumbellIcon from "@components/Icons/DumbellIcon";
import ScaleIcon from "@components/Icons/ScaleIcon";
import WaterIntakeIcon from "@components/Icons/WaterIntakeIcon";

import MealTypeIcon from "../../../Clients/ClientDetails/ClientDiary/Meals/components/MealTypeIcon";

interface props {
  id: string;
}

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    width: "100%",
    marginBottom: 14,
  },
  heading: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 200,
  },
  planBody: {
    display: "flex",
    width: "100%",
  },
  profileInfo: {
    alignItems: "center",
    display: "flex",
    flexDirection: "row",
    marginRight: "2rem",
    width: "40%",
  },
  planDates: {
    display: "flex",
    flexDirection: "row",
    width: "60%",
  },
  planInfo: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  planTitle: {
    fontSize: "1rem",
    fontWeight: 600,
    marginBottom: 4,
  },
  dateLabel: {
    fontSize: theme.typography.pxToRem(12),
    marginRight: 2,
  },
  date: {
    fontSize: theme.typography.pxToRem(12),
  },
  activityIcons: {
    height: theme.typography.pxToRem(28),
    width: theme.typography.pxToRem(28),
  },
  timelineContent: {
    "&:MuiTimelineContent-root": {
      marginTop: 0,
      marginBottom: 0,
      paddingLeft: 16,
    },
  },
}));

const StyledMoreHorizIcon = withStyles(MoreHorizIcon, {
  root: {
    color: colors.primary,
  },
});

const StyledAvatar = withStyles(Avatar, {
  root: {
    marginRight: 10,
  },
});

const StyledAccordionDetails = withStyles(AccordionDetails, {
  root: {
    border: "3px solid #DEE6F0",
  },
});

const StyledAccordion = withStyles(Accordion, {
  root: {
    padding: 4,
  },
});

const StyledTimelineItem = withStyles(TimelineItem, {
  missingOppositeContent: {
    "&:before": {
      display: "none",
    },
  },
});

const StyledTimelineDot = withStyles(TimelineDot, (theme) => ({
  root: {
    display: "flex",
    border: "2px solid #DEE3EA",
    height: theme.typography.pxToRem(40),
    width: theme.typography.pxToRem(40),
    alignItems: "center",
    justifyContent: "center",
    padding: 2,
    marginBottom: 0,
    marginTop: 0,
  },
}));

const StyledTimelineContent = withStyles(TimelineContent, {
  root: {
    paddingLeft: 16,
    paddingTop: 0,
  },
});

export const activeClientCardFragment = graphql(`
  fragment ActiveClientCard on Client {
    id
    profile {
      id
      fullName
      avatarUrl(variant: THUMB)
    }
    programForDate(date: $currentDate) {
      id
      name
      startDate
      endDate
    }
    activities(first: 5) {
      nodes {
        id
        message
        notifiableType
        notifiable {
          id
          ... on Meal {
            id
            mealType
          }
        }
      }
    }
  }
`);

const ActiveClientCard: React.FC<props> = ({ id }) => {
  const { classes } = useStyles();

  const [client] = useFragment(activeClientCardFragment, `Client:${id}`, {
    variables: { currentDate: formatISODate(new Date()) },
  });

  function getActivityIcon(
    activityReference: ActiveClientCardFragment["activities"]["nodes"][0]["notifiable"]
  ) {
    switch (activityReference?.__typename) {
      case "Meal":
        return <MealTypeIcon type={activityReference.mealType} />;
      case "Exercise":
        return <DumbellIcon />;
      case "WaterIntake":
        return <WaterIntakeIcon className={classes.activityIcons} />;
      case "BodyComposition":
      case "BodyMeasurement":
        return <ScaleIcon className={classes.activityIcons} fontSize="large" />;
      case "DietaryHistory":
        return (
          <RestaurantIcon className={classes.activityIcons} fontSize="large" />
        );
    }
  }

  function getActivities() {
    if ((client?.activities.nodes.length ?? 0) > 0) {
      return client?.activities.nodes.map((act) => {
        return (
          <StyledTimelineItem key={act?.id}>
            <TimelineSeparator>
              <StyledTimelineDot variant="outlined">
                {getActivityIcon(act.notifiable)}
              </StyledTimelineDot>
              <TimelineConnector />
            </TimelineSeparator>
            <StyledTimelineContent className={classes.timelineContent}>
              {act.message}
            </StyledTimelineContent>
          </StyledTimelineItem>
        );
      });
    } else {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          padding={14}
        >
          <Typography variant="h6" color="textSecondary">
            This client does not have any activities yet.
          </Typography>
        </Box>
      );
    }
  }

  return (
    <div className={classes.root}>
      <StyledAccordion>
        <AccordionSummary
          expandIcon={<StyledMoreHorizIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Box className={classes.planBody}>
            <Box className={classes.profileInfo}>
              <StyledAvatar src={client?.profile?.avatarUrl ?? undefined} />
              <Typography variant="body1">
                {client?.profile?.fullName}
              </Typography>
            </Box>

            <Box className={classes.planInfo}>
              <Typography className={classes.planTitle}>
                {client?.programForDate?.name ?? "No plan assigned yet"}
              </Typography>
              <Box className={classes.planDates}>
                <Box display="flex" flexDirection="row" marginRight={2}>
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    className={classes.dateLabel}
                  >
                    Start:
                  </Typography>
                  <Typography
                    variant="body1"
                    color="textPrimary"
                    className={classes.date}
                  >
                    {client?.programForDate?.startDate ?? "--"}
                  </Typography>
                </Box>

                <Box display="flex" flexDirection="row" marginRight={2}>
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    className={classes.dateLabel}
                  >
                    End:
                  </Typography>
                  <Typography
                    variant="body1"
                    color="textPrimary"
                    className={classes.date}
                  >
                    {client?.programForDate?.endDate ?? "--"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </AccordionSummary>
        <StyledAccordionDetails>
          <Box display="flex" width="100%" flexDirection="column">
            <Timeline position="right">{getActivities()}</Timeline>
          </Box>
        </StyledAccordionDetails>
      </StyledAccordion>
    </div>
  );
};

export default ActiveClientCard;
